import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";

import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import AspectRatioRoundedIcon from "@mui/icons-material/AspectRatioRounded";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";



// ─── Обычная кнопка ───────────────────────────────────────────────────────────

function NavBtn({ icon, label, onClick, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={(theme) => ({
        minWidth: 45,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "3px",
        py: 0.75,
        [theme.breakpoints.up(400)]: {
          px: 1.5,
        },
        [theme.breakpoints.up(350)]: {
          px: 0.5,
        },
        borderRadius: "14px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.32 : 1,
        transition: "background 0.15s, transform 0.12s",
        WebkitTapHighlightColor: "transparent",
        "&:active": disabled ? {} : {
          transform: "scale(0.91)",
          bgcolor: "rgba(255,255,255,0.18)",
        },
      })}
    >
      <Box sx={{
        display: "flex", alignItems: "center",
        color: disabled ? "rgba(80,90,120,0.5)" : "rgba(50,65,120,0.85)",
        "& svg": { fontSize: 21 },
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontSize: "9.5px", lineHeight: 1, fontWeight: 500,
        color: disabled ? "rgba(80,90,120,0.45)" : "rgba(50,65,120,0.75)",
      }}>
        {label}
      </Typography>
    </Box>
  );
}


// ─── Центральная кнопка + ─────────────────────────────────────────────────────

function AddPageBtn({ onClick }) {
  return (
    <Box
      sx={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "4px",
        px: 1,
      }}
    >
      <Box
        onClick={onClick}
        sx={{
          width: 46, height: 46,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #2451cf 0%, #708ee1 45%, #EFD72C 100%)",
          backgroundSize: "200% 200%",
          animation: "addBtnShimmer 3s ease infinite",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 4px 18px rgba(43,92,230,0.50)",
          transition: "transform 0.15s, box-shadow 0.15s",
          WebkitTapHighlightColor: "transparent",
          "&:active": {
            transform: "scale(0.89)",
            boxShadow: "0 2px 8px rgba(43,92,230,0.35)",
          },
          "@keyframes addBtnShimmer": {
            "0%":   { backgroundPosition: "0% 50%" },
            "50%":  { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <AddRoundedIcon sx={{ color: "white", fontSize: 24 }} />
      </Box>
      <Typography sx={{ fontSize: "9.5px", lineHeight: 1, fontWeight: 500, color: "rgba(50,65,120,0.75)" }}>
        Добавить
      </Typography>
    </Box>
  );
}


// ─── Центральная кнопка + ─────────────────────────────────────────────────────

function PreviewBtn({ onClick, disabled }) {
  return (
    <Box
      sx={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "4px",
        px: 1,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          width: 46, height: 46,
          borderRadius: "50%",
          background: disabled
            ? "rgba(130,140,170,0.35)"
            : "linear-gradient(135deg, #2451cf 0%, #708ee1 45%, #2B5CE6 100%)",
          backgroundSize: "200% 200%",
          animation: disabled ? "none" : "addBtnShimmer 3s ease infinite",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          boxShadow: disabled ? "none" : "0 4px 18px rgba(43,92,230,0.50)",
          transition: "transform 0.15s, box-shadow 0.15s",
          WebkitTapHighlightColor: "transparent",
          "&:active": {
            transform: "scale(0.89)",
            boxShadow: "0 2px 8px rgba(43,92,230,0.35)",
          },
          "@keyframes addBtnShimmer": {
            "0%":   { backgroundPosition: "0% 50%" },
            "50%":  { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <VisibilityRoundedIcon sx={{ color: "white", fontSize: 24 }} />
      </Box>
      <Typography sx={{ fontSize: "9.5px", lineHeight: 1, fontWeight: 500, color: "rgba(50,65,120,0.75)" }}>
        Предпросмотр
      </Typography>
    </Box>
  );
}


// ─── Основной компонент ───────────────────────────────────────────────────────

export default function BottomNav({ onAddPage, onTemplates, onPageSettings, onAddToCart, onChangeFormat, onMyBooks, onPreview, onCheckout, onBackToEditor, isPreviewMode, hasBook, savedBooksCount }) {
  return (
    <Box
      sx={{
        // Фиксированная позиция — левитирует над контентом
        display: "flex", flexDirection: "row",
        alignItems: "center",
        gap: 0.5,
        px: 1.5, py: 0.75,
        zIndex: 500,

        // Пилюля с блюром
        display: "flex", flexDirection: "row",
        alignItems: "center",
        gap: 0.5,
        px: 1.5, py: 0.75,
        borderRadius: 20,

        // Стекло — как у верхних вкладок
        bgcolor: "rgba(180,185,210,0.38)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "0.5px solid rgba(179, 179, 179, 0.55)",
        boxShadow: "0 4px 24px rgba(60,80,160,0.14), 0 1px 0 rgba(255,255,255,0.6) inset",
      }}
    >
      
      {isPreviewMode ? (
        <NavBtn icon={<StyleRoundedIcon />} label="Редактор" onClick={onBackToEditor} />
      ) : (
        <NavBtn icon={<StyleRoundedIcon />} label="Шаблоны" onClick={onTemplates} />
      )}
      {/* <NavBtn icon={<TuneRoundedIcon />} label="Настройки" onClick={onPageSettings} /> */}

      {/* Центральная кнопка: Предпросмотр или Оформить */}
      {isPreviewMode ? (
        <Box
          onClick={onCheckout}
          sx={{
            display: "flex", flexDirection: "row",
            alignItems: "center", justifyContent: "center",
            gap: "6px",
            px: 2, py: 1.5,
            borderRadius: "50px",
            background: "linear-gradient(135deg, #2451cf 0%, #708ee1 45%, #2B5CE6 100%)",
            backgroundSize: "200% 200%",
            animation: "addBtnShimmer 3s ease infinite",
            cursor: "pointer",
            boxShadow: "0 4px 18px rgba(43,92,230,0.50)",
            transition: "transform 0.15s, box-shadow 0.15s",
            WebkitTapHighlightColor: "transparent",
            "&:active": {
              transform: "scale(0.93)",
              boxShadow: "0 2px 8px rgba(43,92,230,0.35)",
            },
            "@keyframes addBtnShimmer": {
              "0%":   { backgroundPosition: "0% 50%" },
              "50%":  { backgroundPosition: "100% 50%" },
              "100%": { backgroundPosition: "0% 50%" },
            },
          }}
        >
          <CheckCircleRoundedIcon sx={{ color: "white", fontSize: 18 }} />
          <Typography level="title-md" sx={{ lineHeight: 1, fontWeight: 700, color: "white", whiteSpace: "nowrap" }}>
            Оформить заказ
          </Typography>
        </Box>
      ) : (
        <PreviewBtn onClick={hasBook ? onPreview : undefined} disabled={!hasBook} />
      )}

      {/* <NavBtn icon={<AspectRatioRoundedIcon />} label="Формат" onClick={onChangeFormat} /> */}
      <NavBtn
        icon={
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <LibraryBooksRoundedIcon />
            {savedBooksCount > 0 && (
              <Box sx={{
                position: "absolute",
                top: -6,
                right: -8,
                minWidth: 15,
                height: 15,
                borderRadius: "50%",
                bgcolor: "#c30013",
                color: "#fff",
                fontSize: "8.5px",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                px: "3px",
                boxShadow: "0 1px 4px rgba(195,0,19,0.4)",
              }}>
                {savedBooksCount}
              </Box>
            )}
          </Box>
        }
        label="Мои книги"
        onClick={onMyBooks}
      />
    </Box>
  );
}